import { Badge } from "@/components/ui/badge";
import { ChevronRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { AssetConstellationMosaic } from "./AssetConstellationMosaic";
import { cn } from "@/lib/utils";

const PACE_TONE = {
  on_track: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ahead:    "bg-sky-100 text-sky-800 border-sky-200",
  behind:   "bg-rose-100 text-rose-800 border-rose-200",
  unknown:  "bg-slate-100 text-slate-700 border-slate-200",
};
const PACE_LABEL = { on_track: "On track", ahead: "Ahead", behind: "Behind", unknown: "—" };

function toDdMmYyyy(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function ParkCycleCard({ park }) {
  const cycle = park.current_cycle || {};
  const pct = Math.max(0, Math.min(100, Math.round(cycle.percent_complete || 0)));
  const paceTone = PACE_TONE[cycle.pace] || PACE_TONE.unknown;
  const hasAssets = (park.assets_count || 0) > 0;
  // Build tile shape compatible with mosaic (mini variant uses progress/state)
  const tiles = (park.mini_tiles || []).map((m) => ({
    asset_id: m.asset_id,
    name: "",
    asset_type: m.asset_type,
    state: m.state,
    progress: m.progress,
    visits: 0,
    length_km: null,
    cleaned_km: null,
    last_date: null,
    neglect_days: null,
  }));
  return (
    <Link
      to={`/admin/cleaning-cycles/${park.park_id}`}
      className="group relative flex flex-col gap-3 rounded-2xl border bg-white p-4 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      data-testid={`park-cycle-card-${park.park_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500">
            <MapPin className="h-3 w-3" />
            <span>{park.park_code || "—"}</span>
            {park.phase ? <><span>·</span><span>{park.phase}</span></> : null}
          </div>
          <div className="font-display text-base font-semibold leading-tight" data-testid={`park-cycle-card-name-${park.park_id}`}>{park.park_name}</div>
        </div>
        <Badge variant="outline" className={cn("shrink-0 border", paceTone)} data-testid={`park-cycle-pace-badge-${park.park_id}`}>{PACE_LABEL[cycle.pace] || "—"}</Badge>
      </div>

      {/* Mini mosaic */}
      {hasAssets ? (
        <div className="flex flex-col gap-2" data-testid={`park-mini-mosaic-${park.park_id}`}>
          <div className="-mx-1 min-h-[72px]">
            <AssetConstellationMosaic tiles={tiles.map((t) => ({...t, name: "asset"}))} dense hideHeaders />
          </div>
          <MiniLegend counts={countByState(tiles)} />
        </div>
      ) : (
        <div className="flex h-[72px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-xs text-slate-500">
          No assets seeded yet
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-1 border-t pt-3">
        <Mini label="Day" value={hasAssets ? `${cycle.duration_days}/${cycle.sla_target_days || "—"}` : "—"} />
        <Mini label="% done" value={hasAssets ? `${pct}%` : "—"} highlight={hasAssets} />
        <Mini label="Overdue" value={hasAssets ? String(park.overdue_count ?? 0) : "—"} tone={park.overdue_count > 0 ? "rose" : "slate"} />
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span>Forecast: <span className="font-mono" data-testid={`park-forecast-date-${park.park_id}`}>{toDdMmYyyy(cycle.forecast_date)}</span></span>
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function Mini({ label, value, tone = "slate", highlight = false }) {
  const toneCls = {
    slate: "text-slate-700",
    rose: "text-rose-700",
    emerald: "text-emerald-700",
  }[tone] || "text-slate-700";
  return (
    <div className="flex flex-col items-center">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={cn("text-sm font-semibold tabular-nums", toneCls, highlight ? "text-amber-700" : "")}>{value}</div>
    </div>
  );
}

function countByState(tiles) {
  const m = { complete: 0, in_progress: 0, not_started: 0, overdue: 0 };
  for (const t of tiles) m[t.state] = (m[t.state] || 0) + 1;
  return m;
}

function MiniLegend({ counts }) {
  const items = [
    { k: "complete",    label: "Done",        tone: "bg-emerald-500", text: "text-emerald-700" },
    { k: "in_progress", label: "In progress", tone: "bg-amber-500",   text: "text-amber-700" },
    { k: "overdue",     label: "Overdue",     tone: "bg-rose-500",    text: "text-rose-700" },
    { k: "not_started", label: "Pending",     tone: "bg-slate-300",   text: "text-slate-600" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
      {items.filter((i) => (counts[i.k] || 0) > 0).map((i) => (
        <span key={i.k} className="inline-flex items-center gap-1">
          <span className={cn("inline-block h-2 w-2 rounded-sm", i.tone)} />
          <span className={cn("font-medium tabular-nums", i.text)}>{counts[i.k]}</span>
          <span className="text-slate-500">{i.label}</span>
        </span>
      ))}
    </div>
  );
}
